import {
  GIFT_FULFILMENT,
  GIFT_FULFILMENT_LABELS,
  MAX_GIFTS_PER_PROGRAM,
  MAX_GIFT_NAME_LENGTH,
  MAX_GIFT_DESCRIPTION_LENGTH,
} from '@affily/shared/constants';
import { rule } from '../lib/fieldRules.js';
import { Button, Field, Input, Select, Textarea, Alert } from './ui.jsx';

/**
 * The gifts section of the program editor.
 *
 * The form holds gifts as strings the way a person types them (dollars, a
 * quantity) and `giftsToPayload` converts to the integer cents and counts the
 * API wants. A gift that already exists carries its `key` through every edit:
 * that key is how the server tells "renamed" from "removed and replaced", and
 * how it keeps the stock already handed out.
 */

export const emptyGift = () => ({
  key: null,
  name: '',
  description: '',
  fulfilment: 'ship',
  retailValue: '',
  quantity: '',
  // Read-only, only meaningful for an existing limited gift.
  remaining: null,
  quantityTotal: null,
});

/** A public gift from the API, as an editable row. */
export const giftToRow = (g) => ({
  key: g.key,
  name: g.name,
  description: g.description ?? '',
  fulfilment: g.fulfilment,
  retailValue: g.retailValueCents != null ? String(g.retailValueCents / 100) : '',
  quantity: g.quantityTotal != null ? String(g.quantityTotal) : '',
  remaining: g.limited ? g.remaining : null,
  quantityTotal: g.limited ? g.quantityTotal : null,
});

/** Editable rows back to what the API accepts. Never includes stock remaining. */
export const giftsToPayload = (rows) =>
  rows.map((g) => ({
    ...(g.key ? { key: g.key } : {}),
    name: g.name,
    description: g.description || undefined,
    fulfilment: g.fulfilment,
    retailValueCents: g.retailValue === '' ? null : Math.round(Number(g.retailValue) * 100),
    quantityTotal: g.quantity === '' ? null : Number(g.quantity),
    isActive: true,
  }));

export function GiftsEditor({ gifts, onChange, fieldErrors = {}, giftOnly = false, currency = 'usd' }) {
  const update = (i, patch) => onChange(gifts.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  const remove = (i) => onChange(gifts.filter((_, idx) => idx !== i));
  const err = (i, field) => fieldErrors[`gifts.${i}.${field}`];

  // `gifts` alone is the array-level message ("needs at least one gift"). It is
  // shown once here rather than on every row, which is what the generic
  // nested-error fallback would do.
  const listError = fieldErrors.gifts;

  return (
    <fieldset className="rounded-lg border border-rule bg-paper-raised p-4">
      <legend className="px-1 text-sm font-semibold">Gifts · published publicly</legend>

      <p className="mt-1 text-xs text-ink-muted">
        {giftOnly
          ? 'This program pays no commission, so the gifts are the whole offer. Add at least one.'
          : 'Free product or experiences offered on top of the commission. Optional.'}{' '}
        Gifts never change what a conversion pays.
      </p>

      <Alert className="mt-3">{listError}</Alert>

      <div className="mt-3 space-y-3">
        {gifts.map((g, i) => (
          <div key={g.key ?? `new-${i}`} className="rounded-md border border-rule bg-paper-sunken p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Gift"
                required
                error={err(i, 'name')}
                constraint={rule.max(MAX_GIFT_NAME_LENGTH)}
              >
                <Input
                  value={g.name}
                  onChange={(e) => update(i, { name: e.target.value })}
                  maxLength={MAX_GIFT_NAME_LENGTH}
                  placeholder="2 nights at our hotel"
                />
              </Field>

              <Field label="How it reaches the creator" required error={err(i, 'fulfilment')}>
                <Select value={g.fulfilment} onChange={(e) => update(i, { fulfilment: e.target.value })}>
                  {GIFT_FULFILMENT.map((f) => (
                    <option key={f} value={f}>
                      {GIFT_FULFILMENT_LABELS[f]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field
              label="Description"
              className="mt-3"
              error={err(i, 'description')}
              constraint={rule.max(MAX_GIFT_DESCRIPTION_LENGTH)}
              hint="What’s included, and any catch — dates, sizes, availability."
            >
              <Textarea
                rows={2}
                value={g.description}
                onChange={(e) => update(i, { description: e.target.value })}
                maxLength={MAX_GIFT_DESCRIPTION_LENGTH}
              />
            </Field>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field
                label={`Retail value (${currency.toUpperCase()})`}
                error={err(i, 'retailValueCents')}
                constraint={`${rule.optional()} · ${rule.money('48.00')}`}
                hint="Shown publicly as brand-stated. Leave blank if there’s no honest price."
              >
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={g.retailValue}
                  onChange={(e) => update(i, { retailValue: e.target.value })}
                />
              </Field>

              <Field
                label="Quantity available"
                error={err(i, 'quantityTotal')}
                constraint={`${rule.optional()} · ${rule.wholeNumber()}`}
                hint="Leave blank for unlimited."
              >
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={g.quantity}
                  onChange={(e) => update(i, { quantity: e.target.value })}
                />
              </Field>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              {g.key && g.quantityTotal != null && g.remaining != null ? (
                <p className="text-xs text-ink-muted">
                  <span className="font-medium text-ink">
                    {g.remaining} of {g.quantityTotal} left
                  </span>{' '}
                  · changing the quantity keeps whatever has already been claimed
                </p>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-xs text-negative hover:underline"
                title={
                  g.key
                    ? 'Retires the gift: it leaves the public page, but creators who already accepted keep it'
                    : undefined
                }
              >
                {g.key ? 'Retire gift' : 'Remove'}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={gifts.length >= MAX_GIFTS_PER_PROGRAM}
          onClick={() => onChange([...gifts, emptyGift()])}
        >
          Add a gift
        </Button>
        <span className="text-xs text-ink-faint">{rule.maxGifts()}</span>
      </div>
    </fieldset>
  );
}
